import React, { useContext, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
    Button,
    Text,
    Flex,
    InfoIcon,
    Input,
    SearchIcon,
    BulletsIcon,
    Popup,
    Divider,
    ArrowRightIcon,
    Alert,
} from "@fluentui/react-northstar";
import { View360Context } from "view360Card/store";
import pl from "shared/messages/pl";
import styles from "./search.module.scss";
import { constants } from "shared/constants";
import { BasicTable, RegularText, TextTableHeader, Title } from "shared/components";
import { MobileSearchEntityResult, SearchEntityTypeIcon } from "view360Card/components";
import { SearchEntity } from "view360Card/models";
import { useNavigate } from "react-router-dom";
import { getLinkToEntity } from "shared/routing/pages";
import { MsTeamsContext } from "msTeams/store";
import OnBehalfOfAuthenticationProvider from "modules/aad-user-details/ms-graph/authentication/OnBehalfOfAuthenticationProvider";

export interface SearchProps {}

export const Search: React.FC<SearchProps> = observer((props) => {
    const view360Context = useContext(View360Context);
    const msTeamsContext = useContext(MsTeamsContext);
    const [query, setQuery] = useState<string | undefined>();
    const searchResultsLimit = 20;
    const navigation = useNavigate();

    useEffect(() => {
        if (view360Context.searchPhrase) {
            setQuery(view360Context.searchPhrase);
        }

        // Pre-fetch Azure access token
        OnBehalfOfAuthenticationProvider.getAccessToken();
    }, []);

    const getHeaderItems = () => {
        let headerItems = [
            {
                className: styles.columnType,
                content: <TextTableHeader content={pl.view360Card.search.resultsTable.type} />,
                key: "type",
            },
            {
                content: <TextTableHeader content={pl.view360Card.search.resultsTable.name} />,
                key: "name",
            },
            {
                className: styles.columnStatus,
                content: <TextTableHeader content={pl.view360Card.search.resultsTable.status} />,
                key: "status",
            },
            {
                content: <TextTableHeader content={pl.view360Card.search.resultsTable.details} />,
                key: "details",
            },
        ];

        if (msTeamsContext.isMobileClient) {
            headerItems.push({
                className: styles.columnAction,
                content: <TextTableHeader content={pl.view360Card.search.resultsTable.action} />,
                key: "actions",
            });
        }

        return headerItems;
    };

    const selectEntityToShow = (searchEntity: SearchEntity) => {
        const linkToEntity = getLinkToEntity(searchEntity.entityType, searchEntity?.entityId ?? 0);
        navigation(linkToEntity);
    };

    const getDetails = (intermediaryList: string[] | undefined) => {
        if (!intermediaryList) {
            return <>{constants.noValue}</>;
        }

        return (
            <Flex column>
                {intermediaryList.map((i, idx) => {
                    return (
                        <div key={i} className={styles.intermediaryOnDetails}>
                            <Flex column>
                                {idx !== 0 && <Divider></Divider>}
                                <RegularText content={i} />
                            </Flex>
                        </div>
                    );
                })}
            </Flex>
        );
    };

    const getItems = (entity: SearchEntity) => {
        let items = [
            {
                className: styles.columnType,
                key: `${entity.entityId}-type`,
                content: <SearchEntityTypeIcon entityType={entity.entityType} />,
            },
            {
                key: `${entity.entityId}-name`,
                content: <RegularText content={entity.name} />,
            },
            {
                className: styles.columnStatus,
                key: `${entity.entityId}-status`,
                content: <RegularText content={entity.status?.description ?? constants.noValue} />,
            },
            {
                key: `${entity.entityId}-details`,
                content: getDetails(entity.intermediaryList),
            },
        ];

        if (msTeamsContext.isMobileClient) {
            items.push({
                className: styles.columnAction,
                key: `${entity.entityId}-action`,
                content: (
                    <Button
                        size="small"
                        primary
                        iconOnly
                        icon={<ArrowRightIcon size="small" />}
                        onClick={() => {
                            selectEntityToShow(entity);
                        }}
                    />
                ),
            });
        }
        return items;
    };

    const getRows = () => {
        return view360Context.searchResults?.entities?.map((entity) => {
            return {
                className: msTeamsContext.currentMsTeamsThemeName === "default" ? styles.defaultRow : styles.darkRow,
                onClick: () => selectEntityToShow(entity),
                key: `${entity.entityType}_${entity.entityId}`,
                items: getItems(entity),
            };
        });
    };

    const runQuery = async () => {
        await view360Context.runQuery(query ?? "", searchResultsLimit);
    };

    const getMobileViewRows = () => {
        if (!view360Context.searchResults?.entities) {
            return null;
        }

        return view360Context.searchResults?.entities.map((entity) => {
            return (
                <MobileSearchEntityResult
                    key={`${entity.entityType}_${entity.entityId}`}
                    entity={entity}
                    onEntitySelected={selectEntityToShow}
                />
            );
        });
    };

    const renderError = () => {
        return (
            <Alert
                className={styles.error}
                danger
                content={pl.view360Card.search.error}
                styles={{ padding: "6px 8px" }}
            />
        );
    };

    const renderResults = () => {
        if (view360Context.isSearchError) {
            return renderError();
        }

        const numberOfResults = view360Context.searchResults?.entities?.length ?? -1;

        return (
            <>
                {numberOfResults >= 0 && (
                    <Title
                        className={styles.title}
                        isHeader
                        vAlign="center"
                        text={pl.view360Card.search.results}
                        icon={<BulletsIcon />}
                    />
                )}
                {view360Context.searchResults?.hasMore && (
                    <Flex hAlign="end" vAlign="center" className={styles.moreResultsAvailable}>
                        <Text temporary>{pl.view360Card.search.moreResultsAvailable(searchResultsLimit)}</Text>
                        <div className={styles.tooltip}>
                            <Popup
                                content={<Text size="small">{pl.view360Card.search.toNarrowResults} </Text>}
                                trigger={<InfoIcon outline />}
                            />
                        </div>
                    </Flex>
                )}
                {numberOfResults === 0 && <Text>{pl.view360Card.search.noResults}</Text>}
                {numberOfResults > 0 && (
                    <BasicTable rows={getRows()} headerItems={getHeaderItems()} mobileViewRows={getMobileViewRows()} />
                )}
            </>
        );
    };

    return (
        <Flex column>
            <Title
                className={styles.title}
                isHeader
                vAlign="center"
                text={pl.view360Card.search.searchTitle}
                icon={<SearchIcon />}
            />
            <Flex column className={styles.searchControls}>
                <Flex vAlign="center" className={styles.searchControlsInputRow}>
                    <Input
                        disabled={view360Context.isSearchInProgress}
                        value={query}
                        onChange={(e) => setQuery((e.currentTarget as EventTarget & HTMLInputElement).value)}
                        fluid
                        onKeyUp={async (event) => {
                            if (event?.keyCode === constants.enterKeyCode) {
                                await runQuery();
                            }
                        }}
                        clearable
                    />
                    <div className={styles.tooltip}>
                        <Popup
                            content={
                                <Flex column>
                                    <Text size="small">{pl.view360Card.search.tooltipLine1}</Text>
                                    <Text size="small">{pl.view360Card.search.tooltipLine2}</Text>
                                </Flex>
                            }
                            trigger={<Button text iconOnly icon={<InfoIcon />} size="small" />}
                        />
                    </div>
                </Flex>
                <Flex vAlign="center" hAlign="end">
                    <Button
                        icon={<SearchIcon />}
                        onClick={runQuery}
                        primary
                        disabled={view360Context.isSearchInProgress}
                        type="submit"
                        content={pl.view360Card.search.search}
                    />
                    <div className={styles.tooltip} />
                </Flex>
            </Flex>

            {!view360Context.isSearchInProgress && renderResults()}
        </Flex>
    );
});
